import { ToastProgrammatic as Toast } from 'buefy';
import dayjs from 'dayjs';
import { saveAs } from 'file-saver';
import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';
import {
  mapActions,
  mapGetters,
  mapMutations,
  mapState,
} from 'vuex';

import { downloadMembersStatisticsReport } from '@/api/reports';
import {
  GET_MEMBER_REPORT_LIST,
  SET_MEMBER_REPORT_RANGE,
  SET_SEARCH_DATE_TYPE,
} from '@/store/reports/constants';
import { GET_DEPARTMENT_LIST } from '@/store/setting/constants';
import {
  DEFAULT_PER_PAGE,
  FIRST_PAGE,
  PER_PAGE_OPTIONS,
  SNACKBAR_TYPES,
} from '@/utils/constants';
import MemberList from '../MemberList/MemberList.vue';

const DATE_SELECTION_OPTIONS = {
  LAST_WEEK: 0,
  THIS_WEEK: 1,
  THIS_MONTH: 2,
  SELF_DEFINE: 3,
};

export default {
  name: 'MemberStatistics',
  components: {
    MemberList,
  },
  props: {
    action: {
      type: String,
      default: 'list',
    },
  },
  data() {
    return {
      activeTab: 'members',
      advancedSearchUnits: [],
      dates: [],
      dateType: 'start',
      dateOptions: [
        {
          label: '上週',
          value: DATE_SELECTION_OPTIONS.LAST_WEEK,
        },
        {
          label: '本週',
          value: DATE_SELECTION_OPTIONS.THIS_WEEK,
        },
        {
          label: '本月',
          value: DATE_SELECTION_OPTIONS.THIS_MONTH,
        },
        {
          label: '自訂',
          value: DATE_SELECTION_OPTIONS.SELF_DEFINE,
        },
      ],
      datePeriodSelection: DATE_SELECTION_OPTIONS.THIS_MONTH,
      exportBtnLoading: false,
      filter: undefined,
      filterTagItems: [
        {
          label: '付清',
          value: 2,
        },
        {
          label: '部分付清',
          value: 1,
        },
        {
          label: '未付款',
          value: 0,
        },
      ],
      isChangingDate: false,
      isFiltered: false,
      openedFilter: false,
      maxDate: null,
      minDate: null,
      page: FIRST_PAGE,
      paymentStatus: [],
      perPage: DEFAULT_PER_PAGE,
      records: [],
    };
  },
  computed: {
    ...mapGetters('reports', ['dateTypeWithRange']),
    ...mapState('reports', {
      dateRange: ({ memberReportRange }) => memberReportRange,
      list: ({ memberReportList }) => memberReportList,
      loading: ({ gettingMemberReportList }) => gettingMemberReportList,
      searchDateType: ({ searchDateType }) => searchDateType,
    }),
    allRecords() {
      return this.list?.data?.records || [];
    },
    disableExport() {
      return !this.formattedRecords?.length || this.isExporting;
    },
    formattedRecords() {
      return this.records?.map(({
        status, total, paid, ...props
      }) => {
        const unpaid = +total - paid;

        return {
          ...props,
          paid,
          status: this.getPaymentStatus({ paid, total }),
          total,
          unpaid,
        };
      }) || [];
    },
    isExporting() {
      return this.exportBtnLoading;
    },
    pagination() {
      return this.list?.pagination || {};
    },
    perPageOptions() {
      return PER_PAGE_OPTIONS;
    },
    total() {
      return this.pagination?.total || 0;
    },
    hasDateRange() {
      const [startDate, endDate] = this.dates;
      return !!startDate && !!endDate;
    },
    userUnits() {
      return this.departments?.map(({ id, name }) => ({ key: id, value: name })) || [];
    },
  },
  beforeDestroy() {
    Object.assign(this.$data, this.$options.data.apply(this));
  },
  async mounted() {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    this.departments = await this.getDepartmentList();
    this.$nextTick(() => {
      this.dateType = this.searchDateType;
      this.dates = this.dateRange?.length ? this.dateRange : [];
      this.setPageOrGetData();
    });
  },
  watch: {
    allRecords: {
      deep: true,
      handler(newVal, oldVal) {
        if (newVal !== oldVal) {
          this.records = cloneDeep(newVal);
        }
      },
    },
    dates(newVal, oldVal) {
      if (
        newVal?.length
        && JSON.parse(JSON.stringify(newVal)) !== JSON.parse(JSON.stringify(oldVal))
      ) {
        const [start, end] = newVal;
        this.datePeriodSelection = this.getSelectionPeriod(start, end);
        this.setReportRange(newVal);
      }
    },
    dateType(newVal) {
      this.setSearchDateType(newVal || 'start');
    },
    dateTypeWithRange(newVal, oldVal) {
      if (!isEqual(newVal, oldVal)) {
        this.setPageOrGetData();
      }
    },
    page() {
      this.getRecords();
    },
    perPage(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.setPageOrGetData();
      }
    },
  },
  methods: {
    ...mapActions('reports', {
      getMemberReportList: GET_MEMBER_REPORT_LIST,
    }),
    ...mapActions('setting', {
      getDepartmentList: GET_DEPARTMENT_LIST,
    }),
    ...mapMutations('reports', {
      setReportRange: SET_MEMBER_REPORT_RANGE,
      setSearchDateType: SET_SEARCH_DATE_TYPE,
    }),
    getParams() {
      const [start, end] = this.dates;
      return {
        date_type: this.dateType,
        department: this.advancedSearchUnits.map(({ key }) => key),
        end_date: dayjs(end).endOf('day').format('YYYY-MM-DD HH:mm'),
        start_date: dayjs(start).startOf('day').format('YYYY-MM-DD HH:mm'),
        page: this.page,
        payment_status: this.paymentStatus,
        per_page: this.perPage,
        name: this.filter,
      };
    },
    async getRecords() {
      try {
        const params = this.getParams();
        await this.getMemberReportList(params);
      } catch (ex) {
        const { message = '' } = ex;

        Toast.open({
          message: `讀取報表時發生錯誤，請稍後再試（${message}）`,
          type: 'is-danger',
        });
      }
    },
    advancedSearchUnitsChange() {
      this.setPageOrGetData();
    },
    dateFormatter(arr) {
      return arr.map((time) => this.formatTime(time, 'YYYY/MM/DD')).join(' - ');
    },
    async exportReport() {
      try {
        const params = this.getParams();
        this.exportBtnLoading = true;
        const res = await downloadMembersStatisticsReport(params);
        const { content = '', filename } = res || {};
        const blob = new Blob([content], { type: 'text/csv' });

        saveAs(blob, filename);
      } catch (ex) {
        this.$snackbar({
          type: SNACKBAR_TYPES.ERROR,
          title: '下載失敗',
          msg: ex.message || '下載時發生未知錯誤，請再試一次',
        });
      } finally {
        this.exportBtnLoading = false;
      }
    },
    getPaymentStatus({ paid = 0, total = 0 }) {
      if (paid >= total) {
        return '付清';
      }
      if (paid <= 0) {
        return '未付款';
      }
      return '部分付清';
    },
    getPeriodWith(period = 'month', date = dayjs()) {
      return {
        end: date.endOf(period),
        start: date.startOf(period),
      };
    },
    getPeriodWithDateFormat(period = 'month', time = dayjs()) {
      const { end, start } = this.getPeriodWith(period, time);
      return [dayjs(start).toDate(), dayjs(end).toDate()];
    },
    getPeriodWithFormat(period = 'month', format = 'YYYY-MM-DD') {
      const { end, start } = this.getPeriodWith(period);

      return {
        end: this.formatTime(end, format),
        start: this.formatTime(start, format),
      };
    },
    getSelectionPeriod(start, end) {
      const [monthStart, monthEnd] = this.getPeriodWithDateFormat('month');
      if (dayjs(start).isSame(monthStart) && dayjs(end).isSame(monthEnd)) {
        return DATE_SELECTION_OPTIONS.THIS_MONTH;
      }
      const [weekStart, weekEnd] = this.getPeriodWithDateFormat('week');
      if (dayjs(start).isSame(weekStart) && dayjs(end).isSame(weekEnd)) {
        return DATE_SELECTION_OPTIONS.THIS_WEEK;
      }
      const [lastWeekStart, lastWeekEnd] = this.getPeriodWithDateFormat(
        'week',
        dayjs().subtract(1, 'week'),
      );
      if (dayjs(start).isSame(lastWeekStart) && dayjs(end).isSame(lastWeekEnd)) {
        return DATE_SELECTION_OPTIONS.THIS_WEEK;
      }

      return DATE_SELECTION_OPTIONS.SELF_DEFINE;
    },
    formatTime(time, format = 'YYYY-MM-DD') {
      return dayjs(time).format(format);
    },
    selectPeriod(value) {
      const DATE_MAPPING = {
        [DATE_SELECTION_OPTIONS.LAST_WEEK]: {
          period: 'week',
          time: dayjs().subtract(1, 'week'),
        },
        [DATE_SELECTION_OPTIONS.THIS_WEEK]: {
          period: 'week',
          time: dayjs(),
        },
        [DATE_SELECTION_OPTIONS.THIS_MONTH]: {
          period: 'month',
          time: dayjs(),
        },
      };
      this.dates = value === DATE_SELECTION_OPTIONS.SELF_DEFINE
        ? this.dates
        : this.getPeriodWithDateFormat(DATE_MAPPING[value].period, DATE_MAPPING[value].time);
    },
    toDetail(memberId) {
      const [start, end] = this.dates;
      window.open(`/statistics/members/${memberId}?start_date=${this.formatTime(start)}&end_date=${this.formatTime(end)}&date_type=${this.dateType}`, '_blank');
    },
    onEndChanges() {
      this.isChangingDate = false;
    },
    onSelectItem({ value }) {
      const index = this.paymentStatus.indexOf(value);
      if (index !== -1) {
        this.paymentStatus.splice(index, 1);
      } else {
        this.paymentStatus.push(value);
      }

      this.setPageOrGetData();
    },
    onStartChanges(date) {
      this.isChangingDate = true;
      this.maxDate = dayjs(date).add(6, 'month').toDate();
      this.minDate = dayjs(date).subtract(6, 'month').toDate();
    },
    onTabChange() {
      this.$router.push('/statistics/group_buyings');
    },
    async onSearch() {
      if (!this.filter) {
        return;
      }
      try {
        this.isFiltered = true;
        this.setPageOrGetData();
      } catch (ex) {
        this.isFiltered = false;
      }
    },
    async onReset() {
      this.isFiltered = false;
      this.filter = '';
      this.setPageOrGetData();
    },
    setPageOrGetData() {
      if (!this.hasDateRange) {
        return;
      }
      this.records = [];
      if (this.page !== FIRST_PAGE) {
        this.page = FIRST_PAGE;
      } else {
        this.getRecords();
      }
    },
  },
};
